import { useCallback, useEffect, useState } from 'react';

import { AppError } from '../../api/errors.js';
import type {
  AccessControlGroup,
  DatasetGroupRoles,
  IAccessControlService,
} from '../../services/accessControlService.interface.js';
import type { UsageContextOverrides } from '../../services/usageContext.interface.js';

import type {
  UseDatasetGroupRoleParams,
  UseDatasetGroupRoleResult,
} from './useDatasetGroupRole.interface.js';

export function useDatasetGroupRole({
  dataSetId,
  service,
}: UseDatasetGroupRoleParams): UseDatasetGroupRoleResult {
  const [ownerGroups, setOwnerGroups] = useState<AccessControlGroup[]>([]);
  const [contributorGroups, setContributorGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [annotatorGroups, setAnnotatorGroups] = useState<AccessControlGroup[]>(
    [],
  );
  const [consumerGroups, setConsumerGroups] = useState<AccessControlGroup[]>(
    [],
  );
  const [restrictedConsumerGroups, setRestrictedConsumerGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<AppError | null>(null);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    async function load() {
      try {
        const groupRole = await service.getDatasetGroupRole(dataSetId);
        if (cancelled) return;

        setHasExistingConfig(true);

        const [
          owners,
          contributors,
          annotators,
          consumers,
          restrictedConsumers,
        ] = await Promise.all([
          resolveAcgs(groupRole.roles.owner, service),
          resolveAcgs(groupRole.roles.contributor, service),
          resolveAcgs(groupRole.roles.annotator, service),
          resolveAcgs(groupRole.roles.consumer, service),
          resolveAcgs(groupRole.roles.restrictedConsumer, service),
        ]);

        if (!cancelled) {
          setOwnerGroups(owners);
          setContributorGroups(contributors);
          setAnnotatorGroups(annotators);
          setConsumerGroups(consumers);
          setRestrictedConsumerGroups(restrictedConsumers);
        }
      } catch (err) {
        if (cancelled) return;
        // 404 means no permissions config yet — treat as empty, not an error.
        if (err instanceof AppError && err.statusCode === 404) {
          setHasExistingConfig(false);
        } else {
          setError(
            err instanceof AppError
              ? err
              : new AppError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to load permissions',
                  'LOAD_ERROR',
                ),
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [service, dataSetId]);

  const save = useCallback(
    async (roles: DatasetGroupRoles, usage?: UsageContextOverrides) => {
      setIsSaving(true);
      setSaveError(null);
      try {
        if (hasExistingConfig) {
          await service.updateDatasetGroupRole(dataSetId, { roles }, usage);
        } else {
          await service.createDatasetGroupRole({ dataSetId, roles }, usage);
          setHasExistingConfig(true);
        }
      } catch (err) {
        const appError =
          err instanceof AppError
            ? err
            : new AppError(
                err instanceof Error
                  ? err.message
                  : 'Failed to save permissions',
                'SAVE_ERROR',
              );
        setSaveError(appError);
        throw appError;
      } finally {
        setIsSaving(false);
      }
    },
    [service, dataSetId, hasExistingConfig],
  );

  return {
    ownerGroups,
    contributorGroups,
    annotatorGroups,
    consumerGroups,
    restrictedConsumerGroups,
    isLoading,
    error,
    save,
    isSaving,
    saveError,
  };
}

async function resolveAcgs(
  ids: string[],
  service: IAccessControlService,
): Promise<AccessControlGroup[]> {
  const results = await Promise.allSettled(
    ids.map((id) => service.getAccessControlGroup(id)),
  );
  return results
    .filter(
      (r): r is PromiseFulfilledResult<AccessControlGroup> =>
        r.status === 'fulfilled',
    )
    .map((r) => r.value);
}
